/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 07-07-2026
 *
 * Concrete hover-groep rond een modelnode. Erft de groep-hover-coördinatie van
 * `BaseNodeOverlayGroepComponent` en declareert de vaste kinderen: de
 * uitleg-tooltip, (sinds substap 2b-3-c, golf 1) de normfeedback- en
 * simfeedback-iconen, en de bouwsuggestie-lampjes.
 */
import {ChangeDetectionStrategy, Component} from '@angular/core';
import {elements} from 'dynalearn-common/lib/elements';
import {DlNodeVolgerDirective} from '../../../directives/dl-node-volger/dl-node-volger.directive';
import {DlNodeDomUitlegComponent} from '../dl-node-dom-uitleg/dl-node-dom-uitleg.component';
import {DlNormfeedbackComponent} from '../dl-normfeedback/dl-normfeedback.component';
import {DlSimfeedbackComponent} from '../dl-simfeedback/dl-simfeedback.component';
import {DlBouwsuggestieComponent} from '../dl-bouwsuggestie/dl-bouwsuggestie.component';
import {BaseNodeOverlayGroepComponent} from '../base-node-overlay-groep.component';

@Component({
    selector: 'dl-modelnode-hovergroep',
    standalone: true,
    imports: [DlNodeVolgerDirective, DlNodeDomUitlegComponent, DlNormfeedbackComponent, DlSimfeedbackComponent, DlBouwsuggestieComponent],
    templateUrl: './dl-modelnode-hovergroep.component.html',
    styleUrls: ['./dl-modelnode-hovergroep.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DlModelnodeHovergroepComponent extends BaseNodeOverlayGroepComponent {

    /**
     * Namen van sub-elementen die, zolang hun ballon open is, de uitleg-tooltip
     * onderdrukken (zie `uitlegZichtbaar`). Alle drie melden zich bij deze groep
     * (zie `opBouwsuggestieHover` en de twee daarboven).
     */
    private static readonly SUBS_ONDERDRUKKEN_UITLEG = ['bouwsuggestie', 'normfeedback', 'simfeedback'];

    /**
     * De nodetypes (en hun subtypes) waarvan de uitleg verder naar rechts hangt: de quantity space en de
     * afgeleide met hun elementen en de waarden. Naast of onder deze nodes staan de labels van de
     * elementen van de quantity space en de afgeleide, en die moet de uitleg vrij laten.
     */
    private static readonly UITLEG_RECHTS_TYPES = ['quantity_space', 'quantity_space_element', 'derivative', 'derivative_element', 'value'];

    constructor() {
        super();
    }

    /**
     * Het nodetype, voor de uitleg-tekstopzoeking.
     */
    protected get nodetype(): string {
        return this.node?.data('type') ?? '';
    }

    /**
     * Of de uitleg verder naar rechts hangt, zodat hij niet over de labels van de elementen valt.
     */
    protected get uitlegNaarRechts(): boolean {
        return DlModelnodeHovergroepComponent.UITLEG_RECHTS_TYPES.some(type => elements.isSubtypeOf(this.nodetype, type));
    }

    /**
     * Het effectief modelleerniveau, voor de uitleg-tekstopzoeking.
     */
    protected get modelleerniveau(): number {
        return this.modelcanvas.getEffectiefModelleerniveau();
    }

    /**
     * Of de uitleg getoond mag worden: uitleg staat aan, de groep is actief, de
     * muis staat niet op een feedback-icoon, en de node is niet geselecteerd
     * (dan is het menu open en onderdrukken we de uitleg).
     */
    protected uitlegZichtbaar(): boolean {
        this.log.debug('uitlegZichtbaar?', this.modelcanvas.isUitlegAan(), this.actief(), this.actieveSub(), this.geselecteerd());
        const actieveSub = this.actieveSub();
        return this.modelcanvas.isUitlegAan()
            && this.actief()
            && (!actieveSub || !DlModelnodeHovergroepComponent.SUBS_ONDERDRUKKEN_UITLEG.includes(actieveSub))
            && !this.geselecteerd();
    }

    /**
     * Verwerkt het open/dicht gaan van de normfeedback-ballon: onderdrukt de
     * uitleg zolang de ballon open is.
     */
    protected opNormfeedbackHover(open: boolean): void {
        if (open) this.meldSub('normfeedback'); else this.wisSub('normfeedback');
    }

    /**
     * Verwerkt het open/dicht gaan van de simfeedback-ballon: onderdrukt de
     * uitleg zolang de ballon open is.
     */
    protected opSimfeedbackHover(open: boolean): void {
        if (open) this.meldSub('simfeedback'); else this.wisSub('simfeedback');
    }

    /**
     * Verwerkt het open/dicht gaan van de bouwsuggestie-ballon: onderdrukt de
     * uitleg zolang de ballon open is.
     */
    protected opBouwsuggestieHover(open: boolean): void {
        if (open) this.meldSub('bouwsuggestie'); else this.wisSub('bouwsuggestie');
    }

    /**
     * Verwerkt een uitleg-actie: houdt de groep-hover bij (muisin/muisuit) en
     * logt elke actie via het canvas.
     */
    protected opUitlegActie(type: string): void {
        if (type === 'uitleg_muisin') {
            this.opGroepEnter();
        } else if (type === 'uitleg_muisuit') {
            this.opGroepLeave();
        }
        this.modelcanvas.logUitlegActie(type, this.node);
    }
}
